import React, { useState, useEffect, useCallback } from 'react';

/** Keys that have already completed typewriter animation - prevents re-animation on remount */
const animatedContentKeys = new Set<string>();

export interface TypewriterTextProps {
    /** Full text to animate */
    text: string;
    /** Delay in ms between each character. Default: 25 */
    speed?: number;
    /** Callback when animation completes */
    onComplete?: () => void;
    /** Additional class names for the container */
    className?: string;
    /** Whether to parse and render **bold** markers. Default: true */
    parseBold?: boolean;
    /** Skip animation and show full text immediately */
    skipAnimation?: boolean;
    /**
     * Stable key for this content. If provided and this key was already animated
     * (e.g. on a previous render/remount), animation is skipped to avoid re-animating.
     */
    contentKey?: string;
}

/**
 * TypewriterText - Animates text with a typewriter effect.
 * Supports **bold** markers for emphasis.
 * Use contentKey to prevent re-animation when the same content is shown again.
 */
const TypewriterText: React.FC<TypewriterTextProps> = ({
    text,
    speed = 25,
    onComplete,
    className,
    parseBold = true,
    skipAnimation = false,
    contentKey,
}) => {
    const alreadyAnimated = contentKey != null && animatedContentKeys.has(contentKey);
    const effectiveSkip = skipAnimation || alreadyAnimated;

    const [visibleLength, setVisibleLength] = useState(effectiveSkip ? text.length : 0);
    const [isComplete, setIsComplete] = useState(effectiveSkip);

    const fullLength = text.length;

    const animate = useCallback(() => {
        if (effectiveSkip || fullLength === 0) {
            setVisibleLength(fullLength);
            setIsComplete(true);
            if (contentKey) animatedContentKeys.add(contentKey);
            onComplete?.();
            return;
        }

        let currentLength = 0;
        const interval = setInterval(() => {
            currentLength += 1;
            setVisibleLength(currentLength);
            if (currentLength >= fullLength) {
                clearInterval(interval);
                setIsComplete(true);
                if (contentKey) animatedContentKeys.add(contentKey);
                onComplete?.();
            }
        }, speed);
        return () => clearInterval(interval);
    }, [text, speed, effectiveSkip, fullLength, contentKey, onComplete]);

    useEffect(() => {
        if (effectiveSkip) {
            setVisibleLength(fullLength);
            setIsComplete(true);
        } else {
            setVisibleLength(0);
            setIsComplete(false);
        }
    }, [text, effectiveSkip, fullLength]);

    useEffect(() => {
        return animate();
    }, [animate]);

    const visibleText = text.slice(0, visibleLength);

    const renderContent = () => {
        if (!parseBold) {
            return <span>{visibleText}</span>;
        }
        return visibleText.split('\n').map((line, index, array) => {
            const parts = line.split(/(\*\*.*?\*\*)/g);
            return (
                <React.Fragment key={index}>
                    {parts.map((part, partIndex) =>
                        part.startsWith('**') && part.endsWith('**') ? (
                            <strong key={partIndex} className="font-semibold">
                                {part.slice(2, -2)}
                            </strong>
                        ) : (
                            <span key={partIndex}>{part}</span>
                        )
                    )}
                    {index < array.length - 1 && <br />}
                </React.Fragment>
            );
        });
    };

    return (
        <span className={className}>
            <span className="whitespace-pre-wrap">{renderContent()}</span>
            {!isComplete && !effectiveSkip && (
                <span
                    className="inline-block w-[2px] h-[1em] bg-[rgba(0,0,0,0.7)] ml-0.5 align-middle animate-pulse"
                    aria-hidden
                />
            )}
        </span>
    );
};

export default TypewriterText;
